"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslations } from "next-intl";
import {
  Conversation,
  Message,
  RoleConversation,
  TypeConversation,
} from "../types/models/conversation";
import {
  EtatTempsReel,
  EvenementSaisie,
  useConversationHub,
} from "../hooks/use-conversation-hub";
import { useKmsService } from "./kms-service-provider";
import { useUser } from "./user-provider";
import { showToast } from "../components/shared/feedback/custom-toast-shortcut";
import { ConversationLabels, nomAuteur } from "../utils/conversation-labels";
import { useNotificationsCourses } from "../hooks/use-notifications-courses";
import { NotificationCourse } from "../types/models/notification-course";

/**
 * État global de la messagerie : la boîte de réception, le compteur de non-lus
 * et le canal temps réel, montés une seule fois pour toute l'application.
 *
 * Le fil ouvert à l'écran garde, lui, son propre état (cf. `use-conversation`)
 * et s'abonne ici pour recevoir ses messages en direct. Ce découpage évite de
 * conserver en mémoire les messages de tous les fils — un gestionnaire les
 * reçoit tous — tout en gardant un compteur juste dans l'entête.
 */

export type AbonnementConversation = {
  onMessage?: (message: Message) => void;
  onSaisie?: (idContact: number) => void;
};

type ConversationsContextType = {
  conversations: Conversation[];
  chargement: boolean;
  /** Somme des non-lus, telle qu'affichée par la pastille de l'entête. */
  totalNonLus: number;
  etatTempsReel: EtatTempsReel;
  /**
   * Incrémenté à chaque rétablissement du canal. Les fils ouverts s'en servent
   * pour rattraper les messages postés pendant la coupure.
   */
  epoqueReconnexion: number;
  rafraichir: () => Promise<void>;
  /** Ouvre un fil, ou retrouve celui qui existe déjà. Idempotent côté serveur. */
  ouvrirConversation: (
    type: TypeConversation,
    idCible: number,
  ) => Promise<Conversation>;
  /**
   * Garantit qu'un fil figure dans la boîte, en l'y ajoutant au besoin. Un lien
   * direct vers une discussion peut viser un fil que la liste n'a pas encore.
   */
  chargerConversation: (idConversation: number) => Promise<void>;
  envoyer: (idConversation: number, contenu: string) => Promise<Message>;
  marquerLu: (idConversation: number) => Promise<void>;
  signalerSaisie: (idConversation: number) => void;
  /** Déclare le fil actuellement à l'écran : il ne produit ni toast ni non-lu. */
  definirConversationActive: (idConversation: number | null) => void;
  souscrire: (
    idConversation: number,
    abonnement: AbonnementConversation,
  ) => () => void;
  /** Suivi des courses, vide hors back-office : les autres n'en reçoivent aucun. */
  notifications: NotificationCourse[];
  /** Notifications arrivées depuis la dernière ouverture de la cloche. */
  nombreNonVues: number;
  /** Marque le fil comme vu jusqu'à la notification la plus récente affichée. */
  marquerNotificationsVues: () => Promise<void>;
};

const ConversationsContext = createContext<ConversationsContextType | null>(
  null,
);

/** Anti-rafale du rechargement de la boîte : un gestionnaire voit passer tous les fils. */
const DELAI_RECHARGEMENT_MS = 800;

/** Espacement minimal entre deux signalements de frappe, comme le demande le hub. */
const INTERVALLE_SAISIE_MS = 3_000;

/** Place le fil touché en tête, comme le fait le tri du serveur. */
const remonterEnTete = (liste: Conversation[], modifiee: Conversation) => [
  modifiee,
  ...liste.filter((conversation) => conversation.id !== modifiee.id),
];

export const ConversationsProvider = ({
  children,
  ouvrirMessagerie,
  ouvrirCourse,
}: {
  children: ReactNode;
  /** Où mène le clic sur une notification. Absent, la notification est passive. */
  ouvrirMessagerie?: (conversation: Conversation) => void;
  /** Idem pour une notification de course. */
  ouvrirCourse?: (idCourse: number) => void;
}) => {
  const { me } = useUser();
  const { conversationService } = useKmsService();
  const t = useTranslations("conversation");

  const labels: ConversationLabels = useMemo(
    () => ({
      roles: {
        [RoleConversation.Passager]: t("role_passenger"),
        [RoleConversation.Demandeur]: t("role_requester"),
        [RoleConversation.Chauffeur]: t("role_driver"),
        [RoleConversation.Gestionnaire]: t("role_manager"),
      },
      roleFallback: t("role_fallback"),
      deletedAccount: t("deleted_account"),
      dispatch: t("dispatch"),
      courseTitle: (id: number | null) => t("course_title", { id: id ?? 0 }),
      courseTitleClient: (id: number | null) =>
        t("course_title_client", { id: id ?? 0 }),
      courseTitleDriver: (id: number | null) =>
        t("course_title_driver", { id: id ?? 0 }),
      courseTitleWithDispatch: (id: number | null) =>
        t("course_title_with_dispatch", { id: id ?? 0 }),
      supportWith: (nom: string) => t("support_with", { nom }),
      supportDefault: t("support_title"),
      noMessages: t("no_message"),
      youPrefix: t("you_prefix"),
      justNow: t("just_now"),
      today: t("today"),
      yesterday: t("yesterday"),
    }),
    [t],
  );

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [chargement, setChargement] = useState(false);
  const [epoqueReconnexion, setEpoqueReconnexion] = useState(0);

  const idContact = me?.idContact ?? null;

  // Refs plutôt qu'états : ces valeurs sont lues par les handlers du hub, qui
  // ne doivent ni provoquer de rendu ni redémarrer la connexion.
  const conversationsRef = useRef<Conversation[]>([]);
  conversationsRef.current = conversations;
  const conversationActiveRef = useRef<number | null>(null);
  const abonnesRef = useRef(new Map<number, Set<AbonnementConversation>>());
  const dernieresSaisiesRef = useRef(new Map<number, number>());
  const rechargementRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ouvrirMessagerieRef = useRef(ouvrirMessagerie);
  ouvrirMessagerieRef.current = ouvrirMessagerie;

  const rafraichir = useCallback(async () => {
    if (!idContact) {
      setConversations([]);
      return;
    }

    setChargement(true);
    try {
      setConversations(await conversationService.getMesConversations());
    } catch (erreur) {
      // La messagerie n'est jamais le motif de la visite : son indisponibilité
      // se signale en console, pas en travers de la page.
      console.warn("Messagerie : boîte de réception indisponible", erreur);
    } finally {
      setChargement(false);
    }
  }, [conversationService, idContact]);

  /** Recharge la boîte, en absorbant les rafales de messages. */
  const planifierRechargement = useCallback(() => {
    if (rechargementRef.current) clearTimeout(rechargementRef.current);
    rechargementRef.current = setTimeout(() => {
      rechargementRef.current = null;
      void rafraichir();
    }, DELAI_RECHARGEMENT_MS);
  }, [rafraichir]);

  useEffect(() => {
    void rafraichir();
  }, [rafraichir]);

  useEffect(
    () => () => {
      if (rechargementRef.current) clearTimeout(rechargementRef.current);
    },
    [],
  );

  const onMessage = useCallback(
    (message: Message) => {
      const estMien = message.idAuteurContact === idContact;
      const estActive =
        conversationActiveRef.current === message.idConversation;

      const connue = conversationsRef.current.find(
        (conversation) => conversation.id === message.idConversation,
      );

      if (connue) {
        setConversations((precedentes) => {
          const cible = precedentes.find(
            (conversation) => conversation.id === message.idConversation,
          );
          if (!cible) return precedentes;

          return remonterEnTete(precedentes, {
            ...cible,
            dernierMessage: message,
            // Le fil affiché est lu au fil de l'eau (cf. `use-conversation`),
            // et ses propres messages ne se comptent pas comme non lus.
            nombreNonLus:
              estMien || estActive ? cible.nombreNonLus : cible.nombreNonLus + 1,
          });
        });
      } else {
        // Fil encore inconnu de la boîte : un gestionnaire reçoit les messages
        // de fils qu'il n'a jamais ouverts, et un participant peut être ajouté
        // à un fil déjà commencé. Dans les deux cas, la boîte est à recharger.
        planifierRechargement();
      }

      abonnesRef.current
        .get(message.idConversation)
        ?.forEach((abonnement) => abonnement.onMessage?.(message));

      if (estMien || estActive) return;

      const cible = connue;
      showToast.message(
        nomAuteur(message, labels),
        message.contenu,
        cible && ouvrirMessagerieRef.current
          ? () => ouvrirMessagerieRef.current?.(cible)
          : undefined,
      );
    },
    [idContact, labels, planifierRechargement],
  );

  const onConversationMiseAJour = useCallback(
    async (idConversation: number) => {
      try {
        const conversation =
          await conversationService.getConversation(idConversation);

        setConversations((precedentes) =>
          precedentes.some((existante) => existante.id === idConversation)
            ? precedentes.map((existante) =>
                existante.id === idConversation ? conversation : existante,
              )
            : [conversation, ...precedentes],
        );
      } catch {
        // Fil devenu inaccessible — un chauffeur remplacé perd l'accès, tout en
        // gardant l'historique côté serveur : on retire la ligne plutôt que de
        // la laisser mener à un 401.
        setConversations((precedentes) =>
          precedentes.filter((existante) => existante.id !== idConversation),
        );
      }
    },
    [conversationService],
  );

  const onSaisie = useCallback(
    ({ idConversation, idContact: auteur }: EvenementSaisie) => {
      if (auteur === idContact) return;

      abonnesRef.current
        .get(idConversation)
        ?.forEach((abonnement) => abonnement.onSaisie?.(auteur));
    },
    [idContact],
  );

  // Le suivi des courses emprunte le même canal que la messagerie — cf. l'entête
  // de `use-conversation-hub` — d'où son branchement ici : ce provider tient la
  // seule connexion au hub, en ouvrir une seconde coûterait un flux permanent
  // de plus pour le même besoin.
  const {
    notifications,
    nombreNonVues,
    onNotificationCourse,
    rafraichir: rafraichirNotifications,
    marquerVues: marquerNotificationsVues,
  } = useNotificationsCourses(ouvrirCourse);

  const onReconnexion = useCallback(() => {
    setEpoqueReconnexion((epoque) => epoque + 1);
    void rafraichir();
    // Ce qui a été diffusé pendant la coupure n'a atteint personne : seule la
    // base en garde trace, d'où le rechargement du fil et pas seulement des fils.
    void rafraichirNotifications();
  }, [rafraichir, rafraichirNotifications]);

  const { etat: etatTempsReel, signalerSaisie: emettreSaisie } =
    useConversationHub(!!idContact, {
      onMessage,
      onConversationMiseAJour,
      onSaisie,
      onNotificationCourse,
      onReconnexion,
    });

  const fusionner = useCallback((conversation: Conversation) => {
    setConversations((precedentes) =>
      precedentes.some((existante) => existante.id === conversation.id)
        ? precedentes.map((existante) =>
            existante.id === conversation.id ? conversation : existante,
          )
        : [conversation, ...precedentes],
    );
  }, []);

  const ouvrirConversation = useCallback(
    async (type: TypeConversation, idCible: number) => {
      const conversation = await conversationService.ouvrir(type, idCible);
      fusionner(conversation);

      return conversation;
    },
    [conversationService, fusionner],
  );

  const chargerConversation = useCallback(
    async (idConversation: number) => {
      if (
        conversationsRef.current.some(
          (conversation) => conversation.id === idConversation,
        )
      ) {
        return;
      }

      try {
        fusionner(await conversationService.getConversation(idConversation));
      } catch (erreur) {
        console.warn("Messagerie : fil inaccessible", erreur);
      }
    },
    [conversationService, fusionner],
  );

  const envoyer = useCallback(
    async (idConversation: number, contenu: string) => {
      const message = await conversationService.poster(idConversation, contenu);

      // Le serveur ne rediffuse pas le message à son auteur : c'est la réponse
      // du POST qui alimente le fil ouvert et la boîte de réception.
      setConversations((precedentes) => {
        const cible = precedentes.find(
          (conversation) => conversation.id === idConversation,
        );
        if (!cible) return precedentes;

        return remonterEnTete(precedentes, {
          ...cible,
          dernierMessage: message,
        });
      });

      abonnesRef.current
        .get(idConversation)
        ?.forEach((abonnement) => abonnement.onMessage?.(message));

      return message;
    },
    [conversationService],
  );

  const marquerLu = useCallback(
    async (idConversation: number) => {
      setConversations((precedentes) =>
        precedentes.map((conversation) =>
          conversation.id === idConversation
            ? { ...conversation, nombreNonLus: 0 }
            : conversation,
        ),
      );

      try {
        await conversationService.marquerLu(idConversation);
      } catch (erreur) {
        console.warn("Messagerie : marque-page non enregistré", erreur);
      }
    },
    [conversationService],
  );

  /**
   * Le hub vérifie l'accès au fil à chaque signalement, donc à chaque lecture :
   * on n'en émet qu'un toutes les quelques secondes de frappe, pas un par
   * touche.
   */
  const signalerSaisie = useCallback(
    (idConversation: number) => {
      const maintenant = Date.now();
      const derniere = dernieresSaisiesRef.current.get(idConversation) ?? 0;
      if (maintenant - derniere < INTERVALLE_SAISIE_MS) return;

      dernieresSaisiesRef.current.set(idConversation, maintenant);
      emettreSaisie(idConversation);
    },
    [emettreSaisie],
  );

  const definirConversationActive = useCallback(
    (idConversation: number | null) => {
      conversationActiveRef.current = idConversation;
    },
    [],
  );

  const souscrire = useCallback(
    (idConversation: number, abonnement: AbonnementConversation) => {
      const abonnes =
        abonnesRef.current.get(idConversation) ??
        new Set<AbonnementConversation>();
      abonnes.add(abonnement);
      abonnesRef.current.set(idConversation, abonnes);

      return () => {
        abonnes.delete(abonnement);
        if (abonnes.size === 0) abonnesRef.current.delete(idConversation);
      };
    },
    [],
  );

  const totalNonLus = useMemo(
    () =>
      conversations.reduce(
        (total, conversation) => total + conversation.nombreNonLus,
        0,
      ),
    [conversations],
  );

  const valeur = useMemo(
    () => ({
      conversations,
      chargement,
      totalNonLus,
      etatTempsReel,
      epoqueReconnexion,
      rafraichir,
      ouvrirConversation,
      chargerConversation,
      envoyer,
      marquerLu,
      signalerSaisie,
      definirConversationActive,
      souscrire,
      notifications,
      nombreNonVues,
      marquerNotificationsVues,
    }),
    [
      conversations,
      chargement,
      totalNonLus,
      etatTempsReel,
      epoqueReconnexion,
      rafraichir,
      ouvrirConversation,
      chargerConversation,
      envoyer,
      marquerLu,
      signalerSaisie,
      definirConversationActive,
      souscrire,
      notifications,
      nombreNonVues,
      marquerNotificationsVues,
    ],
  );

  return (
    <ConversationsContext.Provider value={valeur}>
      {children}
    </ConversationsContext.Provider>
  );
};

export const useConversations = () => {
  const contexte = useContext(ConversationsContext);

  if (!contexte) {
    throw new Error(
      "useConversations doit être utilisé à l'intérieur de ConversationsProvider",
    );
  }

  return contexte;
};

/** Compteur de non-lus d'un fil précis, pour une pastille locale. */
export const useNonLus = (idConversation: number | null) => {
  const { conversations } = useConversations();

  return (
    conversations.find((conversation) => conversation.id === idConversation)
      ?.nombreNonLus ?? 0
  );
};
